import React, { useState, useEffect } from "react";
import {
  History,
  FlaskConical,
  FileText,
  Pill,
  Printer,
  Trash2,
  Calendar,
  User,
  Download,
  ShieldCheck,
  Ban,
  Copy,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { podeGerirPrescricao } from "../../../shared/utils/permissoesCategoria";
import { enviarDocumentoPaciente } from "../services/enviarDocumentoCliente";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  doc,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import {
  gerarPDFPedidoExames,
  gerarPDFTextoLivre,
  gerarPDFReceituario,
  gerarPDFReceituarioComControle,
  gerarPDFReceitaAntimicrobiano,
} from "../services/gerarPDF";
import { baixarPdfBase64 } from "../services/orquestrarAssinatura";

export interface PrescricaoHistorico {
  id: string;
  pacienteId: string;
  tipo:
    | "exames"
    | "texto"
    | "receituario"
    | "receituario-controle"
    | "receituario-controle-eletronica"
    | "receituario-antimicrobiano";
  // B92: presente nos docs tipo "texto" criados a partir de AI_98.
  // Ausente (legado) = laudo_medico.
  subtipo?: "laudo_medico" | "relatorio_multi";
  // B294 (AI_150) — desfecho REAL do envio ao paciente (gravado pelo webhook
  // api/twilio-status.js a partir dos avisos do Twilio). Ausente = enviado antes
  // do B294 ou ainda sem retorno do provedor.
  envioPaciente?: {
    canal: string;
    status: "enviado" | "no-servidor" | "entregue" | "lida" | "nao-entregue";
    codigoErro?: string;
    descricaoErro?: string;
    /** Código de validação do envio — é o que o reenvio precisa. */
    codigo?: string;
  };
  titulo: string;
  conteudo: any; // exames[], texto string, medicamentos[]
  config: {
    nomeAmbulatorio: string;
    nomeMedico: string;
    crm: string;
    endereco: string;
  };
  criadoEm: string;
  criadoPor: string;
  criadoPorNome: string;
  // B212 F5 (AI_127) — true quando há PDF assinado guardado em
  // prescricoes/{id}/assinaturas (subcoleção append-only).
  assinado?: boolean;
  // B323 Bloco 3 (AI_144) — revogação: a prescrição fica no registro,
  // marcada como cancelada, com autor e motivo (nunca some).
  cancelado?: boolean;
  canceladoPorNome?: string;
  canceladoEm?: string;
  motivoCancelamento?: string;
}

// (B88) gate de gestão por categoria — ver permissoesCategoria.ts

interface HistoricoPrescricoesProps {
  paciente: Paciente;
  // Retorna true se abriu o editor (reaproveitar); false se não há sub-aba de
  // editor para o tipo (o card então cai na reimpressão).
  onEditar?: (presc: PrescricaoHistorico) => boolean;
}

export default function HistoricoPrescricoes({
  paciente,
  onEditar,
}: HistoricoPrescricoesProps) {
  const { usuarioLogado } = useAuth();
  const [prescricoes, setPrescricoes] = useState<PrescricaoHistorico[]>([]);
  const [loading, setLoading] = useState(true);
  // B294-FU2 — id da prescrição sendo reenviada (trava o botão).
  const [reenviando, setReenviando] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<
    "todos" | "exames" | "texto" | "receituario"
  >("todos");

  // ============================================
  // CARREGAR HISTÓRICO DO FIREBASE
  // ============================================
  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "prescricoes"),
          where("pacienteId", "==", paciente.id),
          orderBy("criadoEm", "desc")
        );
        const snapshot = await getDocs(q);
        const lista: PrescricaoHistorico[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          criadoEm:
            d.data().criadoEm?.toDate?.()?.toISOString?.() || d.data().criadoEm,
        })) as PrescricaoHistorico[];
        setPrescricoes(lista);
      } catch (error) {
        console.error("Erro ao carregar histórico:", error);
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [paciente.id]);

  // ============================================
  // REGERAR PDF
  // ============================================
  const regerarPDF = (presc: PrescricaoHistorico) => {
    // B323 (AI_144, revisão): uma prescrição CANCELADA não pode ser reimpressa
    // com aparência de documento válido (sairia sem qualquer marca de
    // cancelamento). Bloqueia a reimpressão — o registro fica visível no card
    // (cinza/riscado, com o motivo); para prescrever de novo, emita uma nova.
    if (presc.cancelado) {
      alert(
        "Esta prescrição foi CANCELADA e não pode ser reimpressa. Para prescrever novamente, emita um novo documento."
      );
      return;
    }
    switch (presc.tipo) {
      case "exames":
        gerarPDFPedidoExames(
          paciente,
          presc.conteudo.exames || presc.conteudo,
          presc.config,
          presc.conteudo.observacao
        );
        break;
      case "texto":
        gerarPDFTextoLivre(
          paciente,
          presc.titulo,
          typeof presc.conteudo === "string"
            ? presc.conteudo
            : presc.conteudo.texto || "",
          presc.config
        );
        break;
      case "receituario":
        gerarPDFReceituario(
          paciente,
          presc.conteudo.medicamentos || presc.conteudo,
          presc.config
        );
        break;
      case "receituario-controle":
        gerarPDFReceituarioComControle(
          paciente,
          presc.conteudo.medicamentos || presc.conteudo,
          presc.config
        );
        break;
      case "receituario-antimicrobiano":
        gerarPDFReceitaAntimicrobiano(
          paciente,
          presc.conteudo.medicamentos || presc.conteudo,
          presc.config
        );
        break;
      // B356 (AI_154) — receita eletrônica numerada pelo SNCR: o documento válido
      // é o PDF ASSINADO. Reimprimir como papel geraria uma cópia sem assinatura
      // com o mesmo número do SNCR.
      case "receituario-controle-eletronica":
        alert(
          "Receita eletrônica numerada pelo SNCR: use \"Baixar assinado\" para obter o documento válido."
        );
        break;
    }
  };

  // ============================================
  // REENVIAR AO PACIENTE (B294-FU2, AI_150)
  // ============================================
  // Achado ao vivo de Leonardo (30/08): o selo dizia "✗ Não recebeu — confira o
  // celular no cadastro" e depois NÃO havia ação nenhuma; ele corrigiu o número
  // e teve de emitir uma receita NOVA para o paciente receber. O destravamento
  // do reenvio já existia no servidor (o webhook marca `status: "falha"`, que
  // solta a trava anti-duplicação), mas não tinha caminho na tela — o recurso
  // avisava do problema e escondia a solução.
  //
  // Só duplica se o Twilio tiver dito que a entrega falhou; o envio relê o
  // telefone do cadastro na hora (api/enviar-documento.js), então o número
  // corrigido é o que vale.
  const reenviarAoPaciente = async (presc: PrescricaoHistorico) => {
    const codigo = presc.envioPaciente?.codigo;
    if (!codigo || reenviando) return;
    const seguir = window.confirm(
      "Reenviar este documento ao paciente pelo WhatsApp?\n\n" +
        "Confirme antes que o celular no cadastro já está correto — o envio usa " +
        "o número que estiver salvo agora."
    );
    if (!seguir) return;
    setReenviando(presc.id);
    try {
      const r = await enviarDocumentoPaciente(codigo);
      if (r.ok && r.jaEnviado) {
        // O servidor curto-circuitou (já constava enviado): NADA saiu agora. Dizer
        // "Reenviado" aqui seria mentir — e o selo esconderia o botão.
        alert(
          "Este documento já consta como enviado; nada foi mandado agora. Se o paciente não recebeu, " +
            "emita e assine um novo documento."
        );
      } else if (r.ok) {
        // Selo volta a "enviado" até o provedor confirmar; o webhook atualiza.
        // O canal vem do servidor: se caiu para SMS, o selo não pode alegar WhatsApp.
        setPrescricoes((lista) =>
          lista.map((p) =>
            p.id === presc.id
              ? {
                  ...p,
                  envioPaciente: {
                    ...(p.envioPaciente as any),
                    canal: r.canal || p.envioPaciente?.canal || "whatsapp",
                    status: "enviado",
                    codigoErro: "",
                    descricaoErro: "",
                  },
                }
              : p
          )
        );
        alert(
          r.canal === "sms"
            ? "Reenviado por SMS (o WhatsApp não aceitou). O SMS não tem confirmação de entrega."
            : "Reenviado. A confirmação de entrega chega em instantes — reabra o histórico para conferir o selo."
        );
      } else {
        alert(r.erro || "Não foi possível reenviar agora.");
      }
    } catch (e) {
      alert("Não foi possível reenviar agora.");
    } finally {
      setReenviando(null);
    }
  };

  // ============================================
  // BAIXAR O PDF ASSINADO GUARDADO (B212 F5, AI_127)
  // ============================================
  // Lê o registro mais recente da subcoleção append-only e baixa o PDF assinado
  // (artefato legal). NÃO regenera o PDF (a regeneração mudaria os bytes e
  // invalidaria a assinatura) — entrega exatamente o que foi assinado.
  const baixarAssinado = async (presc: PrescricaoHistorico) => {
    try {
      const q = query(
        collection(db, "prescricoes", presc.id, "assinaturas"),
        orderBy("assinadoEm", "desc"),
        limit(1)
      );
      const snap = await getDocs(q);
      const pdfBase64 = snap.docs[0]?.data()?.pdfBase64 as string | undefined;
      if (!pdfBase64) {
        alert("PDF assinado não encontrado para este documento.");
        return;
      }
      baixarPdfBase64(
        pdfBase64,
        `assinado-${presc.titulo}`.replace(/\s+/g, "_")
      );
    } catch (error) {
      console.error("Erro ao baixar o PDF assinado:", error);
      alert("Não foi possível baixar o PDF assinado.");
    }
  };

  // ============================================
  // EXCLUIR PRESCRIÇÃO
  // ============================================
  // B323 (AI_144) — janela de "engano recente": só o AUTOR pode APAGAR DE VEZ
  // uma prescrição NÃO assinada e NÃO cancelada, nas primeiras 24h. Fora disso,
  // o caminho é CANCELAR (revogar preservando o registro). Espelha a fechadura
  // das firestore.rules; admin (você) apaga em caso excepcional pelo console.
  const HORAS_JANELA_EXCLUSAO = 24;
  const podeApagarDeVez = (presc: PrescricaoHistorico): boolean => {
    if (presc.assinado || presc.cancelado) return false;
    if (!usuarioLogado || presc.criadoPor !== usuarioLogado.id) return false;
    const criado = presc.criadoEm ? new Date(presc.criadoEm) : null;
    if (!criado || isNaN(criado.getTime())) return false;
    const horas = (Date.now() - criado.getTime()) / (1000 * 60 * 60);
    // `<` (não `<=`) para alinhar com a regra do Firestore
    // (request.time < criadoEm + 24h) — evita mostrar a lixeira no instante
    // exato de 24h em que a regra já negaria (revisão AI_144).
    return horas < HORAS_JANELA_EXCLUSAO;
  };

  // Exclusão FÍSICA (some) — só no caso "engano recente do autor" acima.
  const excluirPrescricao = async (id: string) => {
    const atual = prescricoes.find((p) => p.id === id);
    if (!atual || !podeApagarDeVez(atual)) return;
    if (!window.confirm("Apagar de vez esta prescrição recém-criada?")) return;
    try {
      // B213 — captura antes do delete (a lápide sobrevive ao doc)
      await deleteDoc(doc(db, "prescricoes", id));
      setPrescricoes(prescricoes.filter((p) => p.id !== id));

      if (usuarioLogado) {
        const { registrarExclusaoTrilha } = await import(
          "../../../shared/utils/trilhaAuditoria"
        );
        await registrarExclusaoTrilha({
          colecao: "prescricoes",
          docId: id,
          pacienteId: atual?.pacienteId || paciente?.id || "",
          rotulo: atual?.titulo || "Documento",
          usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        });
      }
    } catch (error) {
      // B323 (AI_144, revisão): estado local pode estar defasado (carga única,
      // sem listener; caso multi-janela do B295) e a regra negar o delete. Não
      // falhar em silêncio — avisar o usuário.
      console.error("Erro ao excluir:", error);
      alert(
        "Não foi possível apagar (a prescrição pode já ter sido assinada ou está fora da janela). Atualize a página."
      );
    }
  };

  // B323 Bloco 3 (AI_144) — CANCELAR/REVOGAR: a prescrição fica no registro,
  // marcada como cancelada, com autor e motivo (nunca some — decisão de
  // Leonardo, transparência/auditoria). Vale para assinada E não assinada
  // (revogar é ato clínico; o PDF assinado NÃO é alterado). Qualquer médico
  // pode, com motivo obrigatório (decisão de Leonardo).
  const cancelarPrescricao = async (id: string) => {
    const atual = prescricoes.find((p) => p.id === id);
    if (!atual || atual.cancelado || !usuarioLogado) return;
    const motivo = window.prompt(
      "Motivo do cancelamento desta prescrição (obrigatório):"
    );
    if (motivo == null) return; // cancelou o prompt
    const motivoLimpo = motivo.trim();
    if (!motivoLimpo) {
      alert("É obrigatório informar o motivo do cancelamento.");
      return;
    }
    try {
      await updateDoc(doc(db, "prescricoes", id), {
        cancelado: true,
        canceladoPor: usuarioLogado.id,
        canceladoPorNome: usuarioLogado.nome,
        canceladoEm: serverTimestamp(),
        motivoCancelamento: motivoLimpo,
      });
      setPrescricoes(
        prescricoes.map((p) =>
          p.id === id
            ? {
                ...p,
                cancelado: true,
                canceladoPorNome: usuarioLogado.nome,
                canceladoEm: new Date().toISOString(),
                motivoCancelamento: motivoLimpo,
              }
            : p
        )
      );
      // B213 — trilha de auditoria (Log de Alterações)
      const { registrarEdicaoTrilha } = await import(
        "../../../shared/utils/trilhaAuditoria"
      );
      await registrarEdicaoTrilha({
        colecao: "prescricoes",
        docId: id,
        pacienteId: atual.pacienteId || paciente?.id || "",
        rotulo: `${atual.titulo || "Documento"} — CANCELADA`,
        camposAlterados: ["cancelado"],
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });
    } catch (error) {
      console.error("Erro ao cancelar prescrição:", error);
      alert("Não foi possível cancelar. Tente novamente.");
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getIconeTipo = (tipo: string) => {
    switch (tipo) {
      case "exames":
        return <FlaskConical size={14} color={theme.primary[600]} />;
      case "texto":
        return <FileText size={14} color="#f59e0b" />;
      case "receituario":
      case "receituario-controle":
      case "receituario-controle-eletronica":
      case "receituario-antimicrobiano":
        return <Pill size={14} color="#16a34a" />;
      default:
        return <FileText size={14} />;
    }
  };

  const getLabelTipo = (tipo: string, subtipo?: string) => {
    switch (tipo) {
      case "exames":
        return "Pedido de Exames";
      case "texto":
        return subtipo === "relatorio_multi"
          ? "Relatório Multidisciplinar"
          : "Documento";
      case "receituario":
        return "Receituário";
      case "receituario-controle":
        return "Receituário + Controle Especial";
      case "receituario-antimicrobiano":
        return "Receita de Antimicrobiano";
      case "receituario-controle-eletronica":
        return "Receita de Controle Especial Eletrônica";
      default:
        return tipo;
    }
  };

  const getCorCard = (tipo: string) => {
    switch (tipo) {
      case "exames":
        return { bg: "#eff6ff", border: "#bfdbfe" };
      case "texto":
        return { bg: "#fffbeb", border: "#fde68a" };
      case "receituario":
      case "receituario-controle":
      case "receituario-controle-eletronica":
      case "receituario-antimicrobiano":
        return { bg: "#f0fdf4", border: "#bbf7d0" };
      default:
        return { bg: theme.gray[50], border: theme.gray[200] };
    }
  };

  const getResumo = (presc: PrescricaoHistorico): string[] => {
    switch (presc.tipo) {
      case "exames": {
        const exames = presc.conteudo.exames || presc.conteudo;
        if (Array.isArray(exames)) {
          return exames
            .slice(0, 5)
            .map((e: any, i: number) => `${i + 1}. ${e.nome || e}`);
        }
        return [];
      }
      case "texto":
        const texto =
          typeof presc.conteudo === "string"
            ? presc.conteudo
            : presc.conteudo.texto || "";
        return texto
          ? [texto.substring(0, 80) + (texto.length > 80 ? "..." : "")]
          : [];
      case "receituario":
      case "receituario-controle":
      case "receituario-controle-eletronica":
      case "receituario-antimicrobiano": {
        const meds = presc.conteudo.medicamentos || presc.conteudo;
        if (Array.isArray(meds)) {
          return meds
            .slice(0, 5)
            .map((m: any, i: number) => `${i + 1}. ${m.nome || m}`);
        }
        return [];
      }
      default:
        return [];
    }
  };

  const getTotalItens = (presc: PrescricaoHistorico): number => {
    switch (presc.tipo) {
      case "exames": {
        const exames = presc.conteudo.exames || presc.conteudo;
        return Array.isArray(exames) ? exames.length : 0;
      }
      case "receituario":
      case "receituario-controle":
      case "receituario-controle-eletronica":
      case "receituario-antimicrobiano": {
        const meds = presc.conteudo.medicamentos || presc.conteudo;
        return Array.isArray(meds) ? meds.length : 0;
      }
      default:
        return 0;
    }
  };

  const formatarData = (iso: string): string => {
    try {
      return new Date(iso).toLocaleDateString("pt-BR");
    } catch {
      return iso;
    }
  };

  const prescricoesFiltradas =
    filtroTipo === "todos"
      ? prescricoes
      : prescricoes.filter((p) =>
          filtroTipo === "receituario"
            ? p.tipo === "receituario" ||
              p.tipo === "receituario-controle" ||
              p.tipo === "receituario-controle-eletronica" ||
              p.tipo === "receituario-antimicrobiano"
            : p.tipo === filtroTipo
        );

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "32px",
          color: theme.gray[500],
          fontFamily: theme.fonts.sans,
        }}
      >
        Carregando histórico...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* FILTROS */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            fontWeight: "700",
            color: theme.gray[700],
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <History size={16} />
          {prescricoes.length} documento
          {prescricoes.length !== 1 ? "s" : ""} no histórico
        </span>
        <div style={{ display: "flex", gap: "4px" }}>
          {(
            [
              { id: "todos", label: "Todos" },
              { id: "exames", label: "Exames" },
              { id: "texto", label: "Documentos" },
              { id: "receituario", label: "Receitas" },
            ] as const
          ).map((f) => (
            <button
              key={f.id}
              onClick={() => setFiltroTipo(f.id)}
              style={{
                padding: "3px 10px",
                background:
                  filtroTipo === f.id ? theme.primary[600] : theme.gray[100],
                color: filtroTipo === f.id ? "white" : theme.gray[600],
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* CARDS */}
      {prescricoesFiltradas.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
            color: theme.gray[400],
            fontFamily: theme.fonts.sans,
          }}
        >
          <History
            size={40}
            color={theme.gray[300]}
            style={{ marginBottom: "8px" }}
          />
          <p style={{ fontSize: "13px" }}>
            {prescricoes.length === 0
              ? "Nenhuma prescrição gerada para este paciente"
              : "Nenhum documento encontrado com este filtro"}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "12px",
          }}
        >
          {prescricoesFiltradas.map((presc) => {
            const cor = getCorCard(presc.tipo);
            const resumo = getResumo(presc);
            const total = getTotalItens(presc);
            // Clicar no card abre o editor (reaproveitar) quando há editor para
            // o tipo e a prescrição não está cancelada; senão reimprime.
            const podeReutilizar =
              !!onEditar &&
              podeGerirPrescricao(usuarioLogado, presc) &&
              !presc.cancelado;

            return (
              <div
                key={presc.id}
                style={{
                  // B323 (AI_144): prescrição CANCELADA fica cinza/esmaecida
                  // (decisão de Leonardo: continua visível, transparente).
                  background: presc.cancelado ? theme.gray[100] : cor.bg,
                  border: `1px solid ${
                    presc.cancelado ? theme.gray[300] : cor.border
                  }`,
                  borderRadius: theme.borderRadius.lg,
                  padding: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  position: "relative",
                  opacity: presc.cancelado ? 0.72 : 1,
                }}
                onClick={() => {
                  // Reaproveitar: abre o editor pré-preenchido em vez de já ir
                  // para a impressão. É seguro mesmo com a prescrição ASSINADA —
                  // o editor sempre cria um documento NOVO (addDoc), nunca
                  // sobrescreve o assinado (imutabilidade B323 preservada). Se o
                  // tipo não tem editor, onEditar retorna false e o card imprime.
                  const abriuEditor =
                    onEditar &&
                    podeGerirPrescricao(usuarioLogado, presc) &&
                    !presc.cancelado
                      ? onEditar(presc)
                      : false;
                  if (!abriuEditor) regerarPDF(presc);
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = theme.shadows.md;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {/* HEADER DO CARD */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "2px",
                      }}
                    >
                      {getIconeTipo(presc.tipo)}
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: theme.gray[800],
                          fontFamily: theme.fonts.sans,
                          // B323: título riscado quando cancelada.
                          textDecoration: presc.cancelado
                            ? "line-through"
                            : "none",
                        }}
                      >
                        {presc.tipo === "texto"
                          ? presc.subtipo === "relatorio_multi"
                            ? `${presc.titulo} — Relatório Multidisciplinar`
                            : presc.titulo
                          : getLabelTipo(presc.tipo, presc.subtipo)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "11px",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <Calendar size={10} />
                      {formatarData(presc.criadoEm)}
                      {presc.assinado && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "2px",
                            background: "#dcfce7",
                            color: "#15803d",
                            fontWeight: 700,
                            fontSize: "9px",
                            padding: "1px 5px",
                            borderRadius: "999px",
                          }}
                          title="Assinado digitalmente (PDF guardado)"
                        >
                          <ShieldCheck size={9} /> Assinado
                        </span>
                      )}
                      {(() => {
                        // B294 — selo do envio ao paciente. Verde ✓ entregue,
                        // azul ✓✓ lida, vermelho ✗ não recebeu (com o motivo no
                        // title), cinza enquanto o provedor não confirma.
                        const ep = presc.envioPaciente;
                        if (!ep) return null;
                        const cores: Record<string, { bg: string; fg: string; rotulo: string }> = {
                          lida: { bg: "#dbeafe", fg: "#1d4ed8", rotulo: "✓✓ Lida" },
                          entregue: { bg: "#dcfce7", fg: "#15803d", rotulo: "✓ Entregue" },
                          "nao-entregue": { bg: "#fee2e2", fg: "#b91c1c", rotulo: "✗ Não recebeu" },
                          "no-servidor": { bg: "#f3f4f6", fg: "#6b7280", rotulo: "Aguardando aparelho" },
                          enviado: { bg: "#f3f4f6", fg: "#6b7280", rotulo: "Enviado" },
                        };
                        // SMS não tem confirmação de entrega (a Comtele não avisa):
                        // o selo diz "Enviado por SMS" e para aí — prometer
                        // "aguardando" seria uma espera que nunca termina.
                        const c =
                          ep.status === "enviado" && ep.canal === "sms"
                            ? { bg: "#f3f4f6", fg: "#6b7280", rotulo: "Enviado por SMS" }
                            : cores[ep.status] || cores.enviado;
                        return (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "2px",
                              background: c.bg,
                              color: c.fg,
                              fontWeight: 700,
                              fontSize: "9px",
                              padding: "1px 5px",
                              borderRadius: "999px",
                            }}
                            title={
                              ep.status === "nao-entregue"
                                ? ep.descricaoErro ||
                                  "A mensagem não chegou ao paciente. Confira o celular no cadastro."
                                : `Envio ao paciente por ${ep.canal || "WhatsApp"}`
                            }
                          >
                            {c.rotulo}
                          </span>
                        );
                      })()}
                      {presc.cancelado && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "2px",
                            background: "#fee2e2",
                            color: reenviando && reenviando !== presc.id ? theme.gray[400] : "#b91c1c",
                            fontWeight: 700,
                            fontSize: "9px",
                            padding: "1px 5px",
                            borderRadius: "999px",
                          }}
                          title={
                            presc.motivoCancelamento
                              ? `Cancelada: ${presc.motivoCancelamento}`
                              : "Prescrição cancelada"
                          }
                        >
                          <Ban size={9} /> Cancelada
                        </span>
                      )}
                    </div>
                    {/* B323 (AI_144): motivo do cancelamento à vista (decisão
                        de Leonardo — transparência/auditoria). */}
                    {presc.cancelado && (
                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "10px",
                          color: "#b91c1c",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Cancelada
                        {presc.canceladoPorNome
                          ? ` por ${presc.canceladoPorNome}`
                          : ""}
                        {presc.motivoCancelamento
                          ? ` — ${presc.motivoCancelamento}`
                          : ""}
                      </div>
                    )}
                  </div>
                  {/* AÇÃO DO CARD — B323 (AI_144), gate B88 (podeGerirPrescricao):
                      • já CANCELADA → sem ação (o estado aparece no card);
                      • rascunho recente do AUTOR (não assinado, ≤24h) → APAGAR
                        DE VEZ (lixeira);
                      • demais (emitida/assinada/da colega) → CANCELAR com motivo
                        (revoga preservando o registro). A fechadura real está
                        nas firestore.rules. */}
                  {podeGerirPrescricao(usuarioLogado, presc) &&
                    !presc.cancelado &&
                    (podeApagarDeVez(presc) ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          excluirPrescricao(presc.id);
                        }}
                        style={{
                          padding: "3px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: theme.gray[400],
                          borderRadius: "4px",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.color = "#dc2626")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.color = theme.gray[400])
                        }
                        title="Apagar de vez (rascunho recém-criado)"
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelarPrescricao(presc.id);
                        }}
                        style={{
                          padding: "3px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: theme.gray[400],
                          borderRadius: "4px",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.color = "#b45309")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.color = theme.gray[400])
                        }
                        title="Cancelar / revogar esta prescrição (com motivo)"
                      >
                        <Ban size={12} />
                      </button>
                    ))}
                </div>

                {/* RESUMO */}
                <div
                  style={{
                    fontSize: "11px",
                    color: theme.gray[600],
                    fontFamily: theme.fonts.sans,
                    lineHeight: "1.5",
                  }}
                >
                  {resumo.map((linha, i) => (
                    <div
                      key={i}
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {linha}
                    </div>
                  ))}
                  {total > 5 && (
                    <div
                      style={{
                        textAlign: "center",
                        color: theme.gray[400],
                        fontSize: "14px",
                        marginTop: "2px",
                      }}
                    >
                      ...
                    </div>
                  )}
                </div>

                {/* RODAPÉ DO CARD */}
                <div
                  style={{
                    marginTop: "8px",
                    paddingTop: "6px",
                    borderTop: `1px solid ${cor.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      color: theme.gray[400],
                      fontFamily: theme.fonts.sans,
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    <User size={10} />
                    {presc.criadoPorNome}
                  </span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    {/* B294-FU2 (AI_150) — REENVIAR. Só aparece quando a entrega
                        falhou de fato, no documento NÃO cancelado e para o autor
                        (o endpoint também exige autoria; aqui evitamos oferecer
                        um botão que a equipe clicaria só para receber erro). */}
                    {presc.envioPaciente?.status === "nao-entregue" &&
                      presc.envioPaciente?.codigo &&
                      !presc.cancelado &&
                      usuarioLogado?.id === presc.criadoPor && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            reenviarAoPaciente(presc);
                          }}
                          disabled={!!reenviando}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: reenviando ? "wait" : "pointer",
                            padding: 0,
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#b91c1c",
                            fontFamily: theme.fonts.sans,
                          }}
                          title="Enviar de novo ao paciente (corrija o celular no cadastro antes)"
                        >
                          {reenviando === presc.id ? "Reenviando…" : "↻ Reenviar"}
                        </button>
                      )}
                    {presc.assinado && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          baixarAssinado(presc);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#15803d",
                          fontFamily: theme.fonts.sans,
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                        title="Baixar o PDF assinado guardado"
                      >
                        <Download size={10} />
                        Baixar assinado
                      </button>
                    )}
                    {/* AÇÃO PRINCIPAL DO CARD (AI_153).
                        Para ENFERMEIRO/ADMINISTRATIVO o histórico é de LEITURA:
                        eles não reutilizam nem reenviam — só pegam o PDF da
                        receita/pedido que o médico já fez. Para eles a ação não
                        é "reimprimir" (sugere repetir um ato médico), é BAIXAR,
                        e vira botão de verdade em vez de rótulo passivo.
                        Médico segue com Reutilizar; documento CANCELADO (que não
                        pode sair de novo) segue como rótulo "Reimprimir". */}
                    {!podeGerirPrescricao(usuarioLogado, presc) &&
                    !presc.cancelado ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          regerarPDF(presc);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "10px",
                          fontWeight: 700,
                          color: theme.primary[500],
                          fontFamily: theme.fonts.sans,
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                        title="Abrir o documento para baixar/imprimir o PDF"
                      >
                        <Download size={10} />
                        Baixar PDF
                      </button>
                    ) : (
                      <span
                        style={{
                          fontSize: "10px",
                          color: theme.primary[500],
                          fontFamily: theme.fonts.sans,
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        {podeReutilizar ? (
                          <>
                            <Copy size={10} />
                            Reutilizar
                          </>
                        ) : (
                          <>
                            <Printer size={10} />
                            Reimprimir
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
